import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { TvIcon, WifiIcon, MonitorPlayIcon, ShowerHeadIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
const prices = [{
  type: 'Einzelzimmer',
  price: '49,95 €'
}, {
  type: 'Doppelzimmer',
  price: '69,95 €'
}, {
  type: '4-Bett Zimmer',
  price: '99,95 €'
}];
const amenities = [{
  icon: TvIcon,
  label: 'Flat-TV'
}, {
  icon: WifiIcon,
  label: 'Kostenloses WLAN'
}, {
  icon: MonitorPlayIcon,
  label: 'SKY'
}, {
  icon: ShowerHeadIcon,
  label: 'Dusche'
}];
export function HotelSection() {
  const ref = useRef(null);
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start end', 'end start']
  });
  const y = useTransform(scrollYProgress, [0, 1], ['-10%', '10%']);
  return <section ref={ref} className="py-32 px-4 w-full bg-gradient-to-b from-base to-blue-50/30 relative overflow-hidden" data-id="element-203">
      <div className="max-w-7xl mx-auto" data-id="element-204">
        <div className="text-center mb-16" data-id="element-205">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-5xl md:text-6xl font-heading font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 to-electric-blue" data-id="element-206">
            Erholung auf höchstem Niveau
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-xl text-gray-500 max-w-2xl mx-auto font-body" data-id="element-207">
            60 moderne Zimmer für Ihre perfekte Pause. Buchen Sie direkt und
            unkompliziert.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center" data-id="element-208">
          {/* Image Side */}
          <motion.div initial={{
          opacity: 0,
          x: -40
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="lg:col-span-7 relative aspect-video rounded-[2rem] overflow-hidden" data-id="element-209">
            <motion.div style={{
            y
          }} className="absolute -inset-10" data-id="element-210">
              <img src="https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=900&q=80" alt="Hotelzimmer im Euro Rastpark Himmelkron" className="w-full h-full object-cover" data-id="element-211" />
              <div className="absolute inset-0 bg-gradient-to-t from-blue-50/70 via-transparent to-blue-50/30" data-id="element-212" />
            </motion.div>
          </motion.div>

          {/* Content Side */}
          <div className="lg:col-span-5 flex flex-col gap-6" data-id="element-213">
            <div className="grid grid-cols-1 gap-4" data-id="element-214">
              {prices.map((item, idx) => <motion.div key={item.type} initial={{
              opacity: 0,
              x: 40
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: idx * 0.1
            }} whileHover={{
              scale: 1.02,
              x: 10,
              borderColor: 'rgba(14, 165, 233, 0.5)'
            }} className="glass-card p-6 rounded-2xl flex justify-between items-center border-transparent transition-all" data-id="element-215">
                  <span className="text-lg font-medium text-gray-700" data-id="element-216">
                    {item.type}
                  </span>
                  <span className="text-2xl font-bold text-electric-blue" data-id="element-217">
                    ab {item.price}
                  </span>
                </motion.div>)}
            </div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.4
          }} className="glass-card p-6 rounded-2xl bg-gray-100" data-id="element-218">
              <p className="text-gray-700 mb-2" data-id="element-219">
                Frühstück:{' '}
                <span className="text-gray-900 font-bold" data-id="element-220">9,95 €</span>
              </p>
              <p className="text-gray-500 text-sm" data-id="element-221">Mo–So 6:00 – 11:00 Uhr</p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.5
          }} className="flex flex-wrap gap-4 mt-2" data-id="element-222">
              {amenities.map(amenity => {
              const Icon = amenity.icon;
              return <div key={amenity.label} className="flex items-center gap-2 text-gray-500 text-sm bg-gray-100 px-4 py-2 rounded-full" data-id="element-223">
                    <Icon className="w-4 h-4" data-id="element-224" />
                    {amenity.label}
                  </div>;
            })}
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.6
          }} className="mt-6 flex flex-col sm:flex-row items-center gap-6" data-id="element-225">
              <motion.div whileHover={{
              scale: 1.05,
              boxShadow: '0 0 30px rgba(14, 165, 233, 0.4)'
            }} whileTap={{
              scale: 0.95
            }} className="w-full sm:w-auto rounded-xl" data-id="element-226">
                <Link to="/hotel" className="block w-full text-center px-8 py-4 rounded-xl bg-electric-blue text-white font-semibold text-lg transition-colors" data-id="element-227">
                  Jetzt online buchen
                </Link>
              </motion.div>
              <div className="text-gray-500" data-id="element-228">
                oder telefonisch:
                <br data-id="element-229" />
                <a href="tel:+499273501730" className="text-gray-900 hover:text-electric-blue transition-colors font-medium" data-id="element-230">
                  09273 / 50173-0
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}